import React, { useContext, useMemo } from 'react'
import { useIntl } from 'react-intl'
import { connect } from 'react-redux'
import Pane from 'components/Common/Pane'
import PaneBody from 'components/Common/Pane/PaneBody'
import OfferPricing from 'components/Common/OfferPricing'
import config from 'constants/config'
import OfferTimeLimitedText from '../OfferTimeLimitedText'
import isTimeLimited from 'lib/offer/isTimeLimited'
import WalledContent from 'components/Common/WalledContent'
import TextButton from 'LuxKit/Button/TextButton'
import ClickableLink from 'components/Common/Clickable/ClickableLink'
import { mediaQueryUp, mediaQueryDown } from 'components/utils/breakpoint'
import { rem } from 'polished'
import styled from 'styled-components'
import OfferPartnershipLabel from 'components/Common/Labels/OfferPartnershipLabel'
import { getDepositAmountPercentage, isDepositAvailableOnOffer } from 'lib/payment/depositsUtils'
import SecureWithDepositTagAndTooltip from 'components/Common/SecureWithDeposit/SecureWithDepositTagAndTooltip'
import Heading from 'LuxKit/Typography/Heading'
import CruiseSupportButton from 'components/Cruises/CruiseOfferPage/content/CruiseSupportButton'
import GeoContext from 'contexts/geoContext'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import PriceRowAgentHubCommission from 'agentHub/components/PriceRowAgentHubCommission'
import { getDefaultDepositAmountPercentage } from 'checkout/selectors/featureConfig/deposit'
import { useAppSelector } from 'hooks/reduxHooks'
import Group from 'components/utils/Group'
import { unique } from 'lib/array/arrayUtils'
import HowItWorksItem from 'components/Common/HowItWorks/HowItWorksItem'

const SummaryPane = styled(Pane)`
  border-radius: ${props => props.theme.borderRadius.XS};

  ${mediaQueryUp.tablet} {
    width: 100%;
  }
`

const ButtonWrapper = styled.div`
  ${mediaQueryDown.mobile} {
    @media ( max-height: 500px ) {
      display: none;
    }
  }
`

const SummaryBody = styled(PaneBody).attrs({
  size: 'md',
})`
  > * + * {
    margin-top: ${rem(8)};
  }
`

const StyledSecureDepositTagDiv = styled.div`
  margin-left: ${rem(12)};
`

const StyledOfferTimeLimitedText = styled(OfferTimeLimitedText)`
  margin-left: ${rem(12)};
  padding-bottom: ${rem(4)};
`

const baseHowItWorksItem = {
  contentSize: 'small',
  iconSize: 30,
}

const bestPriceHowItWorksItem = {
  ...baseHowItWorksItem,
  icon: 'handpicked-partner',
  title: 'Best price guarantee',
  content: 'If you find a better price, we’ll match it',
}

interface Props {
  availableForPurchase: boolean
  currentCurrency: string
  offer: App.TourOffer
  price: number
  showPackageOptions: () => void
}

function FlashTourSummary(props: Props) {
  const {
    availableForPurchase,
    currentCurrency,
    offer,
    price,
    showPackageOptions,
  } = props
  const intl = useIntl()

  const showSecureWithDepositTag = isDepositAvailableOnOffer(offer, currentCurrency, undefined, offer.lowestPricePackage.price)
  const isCruiseOffer = offer.holidayTypes?.includes('Cruises')
  const viewAllCruiseOptionsLabel = intl.formatMessage({
    id: 'XhZqzChNaIhFj5awmuIw',
    defaultMessage: 'View All Cruise Options',
    description: 'Button text to view all available cruise package options',
  })
  const viewAllTourOptionsLabel = intl.formatMessage({
    id: 'a05cpWQYOXYUtPIGoGUE',
    defaultMessage: 'View All Tour Options',
    description: 'Button text to view all available tour package options',
  })
  const { partnership } = useContext(GeoContext)
  const defaultDepositAmountPercentage = useAppSelector(getDefaultDepositAmountPercentage)
  const depositAmountPercentage = getDepositAmountPercentage(offer, defaultDepositAmountPercentage)

  const offerLocations = useMemo(() => unique(offer.locations.concat(offer.location)),
    [offer.locations, offer.location])
  const vendorName = offer.vendorName

  return (
    <SummaryPane background="neutral-six" data-testid="SummaryPane">
      <SummaryBody>
        <Pane>
          <PaneBody size="md">
            <VerticalSpacer gap={0}>
              {isTimeLimited(offer) && !isCruiseOffer && <OfferTimeLimitedText />}
              {isTimeLimited(offer) && isCruiseOffer && <StyledOfferTimeLimitedText />}
              <WalledContent
                buttonText="Log in to see price"
                enforced={offer.walledGarden}
              >
                {showSecureWithDepositTag &&
                  <StyledSecureDepositTagDiv>
                    <SecureWithDepositTagAndTooltip offerType={offer.type} depositAmountPercentage={isCruiseOffer ? depositAmountPercentage : 0} />
                  </StyledSecureDepositTagDiv>}
                {price && <VerticalSpacer gap={4}>
                  <OfferPricing offer={offer} />
                  { partnership && partnership.code !== 'velocity' && <OfferPartnershipLabel offer={offer} /> }
                  <PriceRowAgentHubCommission
                    productType={offer.productType}
                    offerId={offer.id}
                    offerLocations={offerLocations}
                    vendorName={vendorName}
                  />
                </VerticalSpacer>}
              </WalledContent>

              <Group direction="vertical" gap={16}>
                {availableForPurchase &&
                  <ButtonWrapper>
                    <TextButton
                      kind="primary"
                      data-testid="OfferAvailable"
                      onClick={showPackageOptions}
                      fit="flex"
                    >
                      {isCruiseOffer ? viewAllCruiseOptionsLabel : viewAllTourOptionsLabel}
                    </TextButton>
                  </ButtonWrapper>}
                {!availableForPurchase &&
                  <Heading variant="heading3" data-testid="OfferSoldOut">
                    {intl.formatMessage({
                      id: 'W4ICj3xA0nWYY3sStjmI',
                      defaultMessage: 'Sold Out',
                      description: 'Heading shown when a flash tour or cruise offer is no longer available',
                    })}
                  </Heading>}

                {isCruiseOffer && !offer.holidayTypes.includes('Ultra Lux') && <CruiseSupportButton />}
              </Group>
            </VerticalSpacer>
          </PaneBody>
        </Pane>
        {config.bestPriceGuarantee &&
          <Group direction="horizontal" horizontalAlign="center">
            <ClickableLink to="/best-price-guarantee">
              <HowItWorksItem item={bestPriceHowItWorksItem} />
            </ClickableLink>
          </Group>}
      </SummaryBody>
    </SummaryPane>
  )
}

const mapStateToProps = (state: App.State) => ({
  currentCurrency: state.geo.currentCurrency,
})

export default connect(mapStateToProps)(FlashTourSummary)
